import { NavLink, useNavigate } from "react-router-dom";
import { Globe2, BookA, LogOut, FlaskConical, LayoutGrid } from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { BusinessSwitcher } from "@/components/BusinessSwitcher";
import { StatusBanners } from "@/components/StatusBanners";
import { Button } from "@/components/ui/button";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end
    data-testid={testid}
    className={({ isActive }) =>
      `flex items-center gap-2.5 px-3 py-2 text-sm rounded-md transition-colors duration-150 ${
        isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="h-4 w-4" /> {label}
  </NavLink>
);

export default function AppShell({ children }) {
  const { session, config, signOut } = useSession();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <aside className="lg:w-60 shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200 bg-white flex lg:flex-col">
        <div className="px-5 py-5 flex items-center gap-2" data-testid="brand-logo">
          <div className="h-8 w-8 rounded-md bg-slate-900 text-white grid place-items-center font-mono text-[11px] font-medium">es</div>
          <div>
            <div className="font-display font-bold tracking-tight leading-none">Morr Translate</div>
            <div className="font-mono text-[10px] text-slate-500 mt-1">EN → ES</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 px-3 py-2 flex-1">
          {(session.tenant?.tenants || []).length > 1 && <NavItem to="/clients" icon={LayoutGrid} label="All clients" testid="nav-all-clients" />}
          <NavItem to="/" icon={Globe2} label="Websites" testid="nav-websites" />
          <NavItem to="/glossary" icon={BookA} label="Glossary" testid="nav-glossary" />
        </nav>
        <div className="hidden lg:block p-4 border-t border-slate-200 text-xs text-slate-500">
          <div className="font-medium text-slate-800 truncate" data-testid="user-name">{session.user?.name || session.user?.email}</div>
          <div className="truncate">{session.user?.email}</div>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 sm:px-8 gap-3">
          <BusinessSwitcher />
          <div className="flex items-center gap-2">
            {config?.demo_mode && (
              <span data-testid="demo-mode-badge" className="inline-flex items-center gap-1.5 text-xs font-mono px-2.5 py-1 rounded-full border border-amber-300 bg-amber-50 text-amber-800">
                <FlaskConical className="h-3.5 w-3.5" /> DEMO MODE
              </span>
            )}
            <Button variant="ghost" size="sm" data-testid="sign-out-button" onClick={() => { signOut(); navigate("/login"); }}>
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </header>
        <StatusBanners />
        <main className="flex-1 px-4 sm:px-8 py-8 max-w-[1400px] w-full">{children}</main>
      </div>
    </div>
  );
}
